import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import { SmartAlertService, SmartAlertGroup, SmartAlertDetail } from '../../service/smart_alert.service';

@Component({
  selector: 'app-smart-alert',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './smart-alert.component.html',
  styleUrls: ['./smart-alert.component.scss']
})
export class SmartAlertComponent implements OnInit {
  private alertService = inject(SmartAlertService);

  visible = false;
  loading = false;
  saving = false;
  errorMessage = '';

  totalCount = 0;
  alerts: SmartAlertGroup[] = [];
  overallRemark = '';

  ngOnInit(): void {
    this.loadAlerts();
  }

  loadAlerts(): void {
    this.loading = true;
    this.errorMessage = '';

    this.alertService
      .getAlerts()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.alerts = (response.smart_alerts ?? []).map((alert) => ({
            ...alert,
            expanded: false
          }));
          this.totalCount = response.total_count ?? 0;
          this.overallRemark = response.overall_remark ?? '';
          this.visible = response.show_popup;
        },
        error: () => {
          this.errorMessage = 'Unable to load Smart Alerts. Please try again.';
        }
      });
  }

  trackAlert(index: number, alert: SmartAlertGroup): number {
    return alert.activity_id;
  }

  trackDetail(index: number, detail: SmartAlertDetail): number {
    return detail.cbr_id;
  }

  save(): void {
    this.submit('save');
  }

  close(): void {
    this.submit('dismiss');
  }

  private submit(action: 'save' | 'dismiss'): void {
    if (this.saving) {
      return;
    }

    // Send only activity remarks containing actual text.
    const activityRemarks =
      action === 'save'
        ? this.alerts.flatMap((alert) =>
            alert.details
              .filter((detail) => (detail.remark ?? '').trim().length > 0)
              .map((detail) => ({
                activity_id: alert.activity_id,
                cbr_id: detail.cbr_id,
                remark: detail.remark.trim()
              }))
          )
        : [];

    this.saving = true;
    this.errorMessage = '';

    this.alertService
      .saveAlerts({
        action,
        ...(action === 'save'
          ? {
              activity_remarks: activityRemarks,
              overall_remark: this.overallRemark
            }
          : {})
      })
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.visible = false;
        },
        error: () => {
          this.errorMessage = 'Unable to save Smart Alerts. Please try again.';
        }
      });
  }
}
